/**
 * HDRI Loader
 * 
 * Loads HDR environment maps and generates prefiltered mipmapped
 * radiance environment maps (PMREM) for PBR lighting.
 */

import * as THREE from 'three';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import type { IAssetLoader } from '../types';

export interface HDRIResult {
  /** Original HDR texture (equirectangular) */
  hdrTexture: THREE.DataTexture;
  
  /** Prefiltered environment map for PBR materials */
  envMap: THREE.Texture;
  
  /** Same as envMap, ready to use as scene.background */
  backgroundTexture: THREE.Texture;
}

export class HDRILoader implements IAssetLoader<HDRIResult> {
  private rgbeLoader: RGBELoader;
  private pmremGenerator: THREE.PMREMGenerator | null = null;
  private cache = new Map<string, HDRIResult>();
  
  constructor() {
    this.rgbeLoader = new RGBELoader();
  }
  
  /**
   * Initialize PMREM generator (requires renderer)
   */
  public initPMREM(renderer: THREE.WebGLRenderer): void {
    if (this.pmremGenerator) {
      this.pmremGenerator.dispose();
    }
    this.pmremGenerator = new THREE.PMREMGenerator(renderer);
    this.pmremGenerator.compileEquirectangularShader();
  }
  
  /**
   * Load an HDR environment map
   */
  public async load(
    url: string,
    onProgress?: (progress: number) => void
  ): Promise<HDRIResult> {
    // Check cache
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }
    
    if (!this.pmremGenerator) {
      throw new Error('PMREM generator not initialized. Call initPMREM(renderer) first.');
    }
    
    return new Promise((resolve, reject) => {
      this.rgbeLoader.load(
        url,
        (hdrTexture) => {
          try {
            // Generate environment map from HDR
            const envMap = this.pmremGenerator!.fromEquirectangular(hdrTexture).texture;
            envMap.colorSpace = THREE.SRGBColorSpace;
            
            const result: HDRIResult = {
              hdrTexture,
              envMap,
              backgroundTexture: envMap,
            };
            
            this.cache.set(url, result);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        (event) => {
          if (onProgress && event.lengthComputable) {
            onProgress(event.loaded / event.total);
          }
        },
        (error) => {
          reject(new Error(`Failed to load HDRI: ${url} - ${error}`));
        }
      );
    });
  }
  
  /**
   * Create environment map from a cube texture
   */
  public fromCubeTexture(cubeTexture: THREE.CubeTexture): THREE.Texture {
    if (!this.pmremGenerator) {
      throw new Error('PMREM generator not initialized');
    }
    return this.pmremGenerator.fromCubemap(cubeTexture).texture;
  }
  
  /**
   * Create a simple gradient environment (no HDR needed)
   */
  public createGradientEnvironment(
    topColor: THREE.Color,
    bottomColor: THREE.Color,
    _renderer: THREE.WebGLRenderer
  ): THREE.Texture {
    // Create a small data texture for the gradient
    const size = 64;
    const data = new Float32Array(size * size * 4);
    
    for (let y = 0; y < size; y++) {
      const t = y / (size - 1);
      const color = new THREE.Color().lerpColors(bottomColor, topColor, t);
      
      for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4;
        data[i] = color.r;
        data[i + 1] = color.g;
        data[i + 2] = color.b;
        data[i + 3] = 1;
      }
    }
    
    const texture = new THREE.DataTexture(
      data,
      size,
      size,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.needsUpdate = true;
    
    // Convert to PMREM
    if (this.pmremGenerator) {
      return this.pmremGenerator.fromEquirectangular(texture).texture;
    }
    
    return texture;
  }
  
  /**
   * Dispose all resources
   */
  public dispose(): void {
    for (const result of this.cache.values()) {
      result.hdrTexture.dispose();
      result.envMap.dispose();
    }
    this.cache.clear();
    
    if (this.pmremGenerator) {
      this.pmremGenerator.dispose();
      this.pmremGenerator = null;
    }
  }
}
